import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Constants } from "@eagerdog/constants";
import { IEvent, IShow, BreedGroupJudgeDto, IQuery, IBreed, BreedJudgeDto, judgesListedOnShow } from "@eagerdog/interfaces";

import { apiService } from "src/services";

import { toast } from "src/components/Toast/ToastManager";
import Input from "src/components/Input/Input";
import Checkbox, { ICheck } from "src/components/Checkbox/Checkbox";
import SearchDown from "src/components/SearchDown/SearchDown";
import Button from "src/components/Button/Button";
import Accordion, { Bellow, useAccordion } from "src/components/Accordion/Accordion";

import { INestedShowElement, nestedElementSetupShows, getShowElementsByNested, shouldUseNestedSetup } from "../utils";

import styles from "./JudgesTab.module.scss";

interface IProps {
  event: IEvent,
  show?: IShow,
  showElements: ICheck[],
  showElementsNested: INestedShowElement[],
  showType: string,
  specialtyBreed?: string,
  readOnly?: boolean,
  onNavigateToJudgeSchedules?: () => void,
  onChange(judges: IJudge[], useSameJudge: boolean, breedGroups: BreedGroupJudgeDto[], breedSpecific: BreedJudgeDto[]): void
}

export interface IJudge {
  judge_element: string,
  judge_level?: string,
  judge_section?: string,
  judge_name: string,
  judge_number: string,
  breed_group?: string,
  backup_judge_name?: string,
  backup_judge_number?: string
}



enum breedErrors {
  NO_DATA
}

export const judgeArrangements = {
  element: "Element",
  breed_group: "Breed Group"
};

// UKC Conformation non-licensed classes that are always judged by the same judge
// who does the regular/altered breed judging for that dog — they never get their
// own dedicated judge input in this tab.
const HIDDEN_JUDGE_ELEMENTS = new Set<string>([
  Constants.dog_class_element.total_dog,
  Constants.dog_class_element.novice_puppy,
  Constants.dog_class_element.veteran,
]);

// UKC breed groups that don't get a Breed Group Judge
const UKC_HIDDEN_JUDGE_BREED_GROUPS = new Set<string>([
  "Bloodlines Listing Service",
  "Unrecognized",
  "Unrecognized (All American)",
]);

// Friendlier display labels for element judge inputs that otherwise read ambiguously
// (e.g. "Conformation Judge Name" doesn't make clear it's the fallback/default judge
// used when no breed or breed group judge is assigned).
const ELEMENT_JUDGE_LABEL_OVERRIDES: Record<string, string> = {
  [Constants.dog_class_element.conformation]: "Default Conformation",
  [Constants.dog_class_element.conformation_altered]: "Default Altered",
};

const getJudgeElementLabel = (element: string): string => {
  return ELEMENT_JUDGE_LABEL_OVERRIDES[element] ?? element;
};

const JudgesTab: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [showElements, setShowElements] = useState<ICheck[]>(props.showElements);

  const [breedGroups, setBreedGroups] = useState<BreedGroupJudgeDto[]>([]);

  const bellows:string[] = ["Element Judges", "Breed Group Judges", "Breed Specific Judges", "Backup Judges"];
  const { switchToBellow, activeBellow } = useAccordion();

  const [breedList, setBreedList] = useState<string[]>([]);
  const [breedValue, setBreedValue] = useState<string>("");
  const [breedExists, setBreedExists] = useState<boolean>(false);

  const [bsJudgeName, setBsJudgeName] = useState<string>("");
  const [bsJudgeNumber, setBsJudgeNumber] = useState<string>("");
  const [bsList, setBsList] = useState<BreedJudgeDto[]>(props.show?.breed_judges ? props.show?.breed_judges : []);
  const [bsInputsKey, setBsInputsKey] = useState<number>(0);

  const isUsingSameJudge = (elements: any) => {
    let judge:any = { 
      judge_name: "", 
      judge_number: "",
      backup_judge_name: "",
      backup_judge_number: ""
    };

    for (let i in elements) {
      if (judge.judge_name === "" && judge.judge_number === "" && judge.backup_judge_name === "" && judge.backup_judge_number === "") {
        judge = { 
          judge_name: elements[i].judge_name, 
          judge_number: elements[i].judge_number,
          backup_judge_name: elements[i].backup_judge_name,
          backup_judge_number: elements[i].backup_judge_number
        };
      } else {
        if (elements[i].judge_name !== judge.judge_name || 
            elements[i].judge_number !== judge.judge_number ||
            elements[i].backup_judge_name !== judge.backup_judge_name ||
            elements[i].backup_judge_number !== judge.backup_judge_number) {
          return false;
        }
      }
    }

    return true;
  }

  const [useSameJudge, setUseSameJudge] = useState<boolean>(props.show ? isUsingSameJudge(props.show.show_elements) : true);
  const [judges, setJudges] = useState<IJudge[]>([]);

  // Preserves same-judge values when the user temporarily unchecks → re-checks the toggle
  const sameJudgeValuesRef = React.useRef({ judge_name: "", judge_number: "", backup_judge_name: "", backup_judge_number: "" });


  let ignoreJudges:string[] = useMemo(() => {
    return [
      Constants.show_type.fast_cat,
      Constants.show_type.fast_dash,
      Constants.show_type.canine_good_citizen,
    ];
  }, []);

  const hasBackupJudges = (): boolean => {
    return props.event.sanctioning_club === Constants.sanctioning_club.UKC;
  }

  const updateJudge = (judgeInfo: any, index: number) => {
    let _judges:IJudge[] = [...judges];

    _judges[index] = { ..._judges[index], ...judgeInfo };

    setJudges(_judges);
  }

  const updateBackupJudge = (judgeInfo: any, index: number) => {
    let _judges:IJudge[] = [...judges];

    _judges[index] = { ..._judges[index], ...judgeInfo };

    setJudges(_judges);
  }

  const updateBreedGroupJudge = (judgeInfo: any, index: number) => {
    let _breeds:BreedGroupJudgeDto[] = [...breedGroups];

    _breeds[index] = { ..._breeds[index], ...judgeInfo };

    setBreedGroups(_breeds);
  }

  const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const MAX_SPLITS = 3;

  // Returns the default split ranges for N judges
  const getDefaultSplitRanges = (n: number): { start: string; end: string }[] => {
    const splits = Math.ceil(26 / n);
    const ranges: { start: string; end: string }[] = [];
    for (let i = 0; i < n; i++) {
      const startIdx = i * splits;
      const endIdx = Math.min(startIdx + splits - 1, 25);
      ranges.push({ start: LETTERS[startIdx], end: LETTERS[endIdx] });
    }
    // Ensure last range always ends at Z
    if (ranges.length > 0) ranges[ranges.length - 1].end = "Z";
    return ranges;
  };

  const addBreedGroupRow = (breedGroup: string) => {
    const _breeds = [...breedGroups];
    // Find all rows for this group
    const groupIndices = _breeds
      .map((b, i) => (b.breed_group === breedGroup ? i : -1))
      .filter(i => i >= 0);

    if (groupIndices.length >= MAX_SPLITS) return;

    const newCount = groupIndices.length + 1;
    const ranges = getDefaultSplitRanges(newCount);

    // Update existing rows with new ranges
    groupIndices.forEach((idx, ri) => {
      _breeds[idx] = { ..._breeds[idx], start_letter: ranges[ri].start, end_letter: ranges[ri].end };
    });

    // Find insertion point (after last row of this group)
    const lastIdx = groupIndices[groupIndices.length - 1];
    const newRow: BreedGroupJudgeDto = {
      breed_group: breedGroup,
      judge_name: "",
      judge_number: "",
      start_letter: ranges[newCount - 1].start,
      end_letter: ranges[newCount - 1].end,
    };
    _breeds.splice(lastIdx + 1, 0, newRow);
    setBreedGroups(_breeds);
  };

  const removeBreedGroupRow = (index: number) => {
    const _breeds = [...breedGroups];
    const breedGroup = _breeds[index].breed_group;
    _breeds.splice(index, 1);

    // How many rows remain for this group?
    const remaining = _breeds
      .map((b, i) => (b.breed_group === breedGroup ? i : -1))
      .filter(i => i >= 0);

    if (remaining.length === 1) {
      // Revert to single-judge (clear letter range)
      _breeds[remaining[0]] = { ..._breeds[remaining[0]], start_letter: undefined, end_letter: undefined };
    } else if (remaining.length > 1) {
      // Redistribute ranges
      const ranges = getDefaultSplitRanges(remaining.length);
      remaining.forEach((idx, ri) => {
        _breeds[idx] = { ..._breeds[idx], start_letter: ranges[ri].start, end_letter: ranges[ri].end };
      });
    }
    setBreedGroups(_breeds);
  };

  const updateBreedGroupLetter = (index: number, field: "start_letter" | "end_letter", value: string) => {
    const _breeds = [...breedGroups];
    _breeds[index] = { ..._breeds[index], [field]: value };

    // If end_letter changed, auto-update the next sibling row's start_letter
    if (field === "end_letter") {
      const breedGroup = _breeds[index].breed_group;
      const groupIndices = _breeds
        .map((b, i) => (b.breed_group === breedGroup ? i : -1))
        .filter(i => i >= 0);
      const posInGroup = groupIndices.indexOf(index);
      if (posInGroup >= 0 && posInGroup < groupIndices.length - 1) {
        const nextIdx = groupIndices[posInGroup + 1];
        const nextStartIdx = LETTERS.indexOf(value) + 1;
        if (nextStartIdx < LETTERS.length) {
          _breeds[nextIdx] = { ..._breeds[nextIdx], start_letter: LETTERS[nextStartIdx] };
        }
      }
    }
    setBreedGroups(_breeds);
  };


  const searchBreeds = (value:string) => {
    if (value.length > 0) {
      let query:IQuery = {
        $limit: 10,
        query: {
          $and: [
            {
              attribute_name: "breed",
              attribute_value: {
                operator: "$regex",
                value: value
              }
            }
          ]
        }
      };

      apiService.getBreeds(query).then((response) => {
        let breeds:string[] = []; 

        response.forEach((b: IBreed) => {
          breeds.push(b.breed);
        });

        setBreedList(breeds);
      });
    } else {
      setBreedList([]);
    }
  }

  const addBreedSpecificJudge = (breed: string, name: string, number: string) => {
    if (breedExists) {
      let _bsList:BreedJudgeDto[] = [...bsList];

      _bsList.push({
        breed: breed,
        judge_name: name,
        judge_number: number
      });

      setBsList(_bsList);

      setBsJudgeName("");
      setBsJudgeNumber("");
      setBreedValue("");
      setBreedExists(false);
      setBreedList([]);
      setBsInputsKey(k => k + 1);
    } else {
      toast.show({
        title: "Breed Specific Judge",
        content: "Please select a breed from the list",
        duration: 10000,
        type: "fail"
      });
    }
  }

  const deleteBreedSpecificJudge = (index: number) => {
    let _bsList:BreedJudgeDto[] = [...bsList];

    _bsList.splice(index, 1);

    setBsList(_bsList);
  }

  const usesBreedGroups = () => {
    // UKC Conformation always uses breed groups
    if (props.event.sanctioning_club === Constants.sanctioning_club.UKC) {
      if (props.showType === Constants.show_type.conformation) {
        return true;
      }
    }

    // AKC Conformation uses breed groups when there's no specialty breed
    if (props.event.sanctioning_club === Constants.sanctioning_club.AKC) {
      if (props.showType === Constants.show_type.conformation) {
        // Check specialty_breed from props first (for new shows), then from show (for editing)
        const specialtyBreed = props.specialtyBreed || props.show?.specialty_breed || '';
        const hasSpecialtyBreed = specialtyBreed.trim() !== '';
        return !hasSpecialtyBreed;
      }
    }

    return false;
  }

  const allowEditForConformation = () => {
    return (
      props.showType === Constants.show_type.conformation &&
      [
        Constants.sanctioning_club.AKC,
        Constants.sanctioning_club.UKC,
      ].includes(props.event.sanctioning_club)
    );
  };

  useEffect(() => {
    setShowElements(props.showElements);

    const getJudges = () => {
      return props.showElements.map((se: any) => {
        let hasPreviousShowElement = props.show?.show_elements.filter((pse: any) => { return se.label === pse.show_element || se.label === pse.level; });

        if (hasPreviousShowElement && hasPreviousShowElement.length > 0) {
          let _pse:any = hasPreviousShowElement[0];

          return {
            judge_element: _pse.level ? _pse.level : _pse.show_element,
            judge_name: _pse.judge_name || "",
            judge_number: _pse.judge_number || "",
            backup_judge_name: _pse.backup_judge_name || "",
            backup_judge_number: _pse.backup_judge_number || ""
          };
        } else {
          return {
            judge_element: se.label,
            judge_name: se.judge_name || "",
            judge_number: se.judge_number || "",
            backup_judge_name: se.backup_judge_name || "",
            backup_judge_number: se.backup_judge_number || ""
          };
        }
      });
    }

    if (ignoreJudges.includes(props.showType)) {      
      let _judges:IJudge[] = [];
      
      _judges = 
        [{
            judge_element: "N/A",
            judge_name: "N/A",
            judge_number: "",
            backup_judge_name: "",
            backup_judge_number: ""
          }];

      setJudges(_judges);

    } else {
    
      if (useSameJudge) {
        let _judges:IJudge[] = [];

        if (props.show) {
          const firstElement = props.show.show_elements?.[0];
          _judges = [{
            judge_element: "",
            judge_name: firstElement?.judge_name || "",
            judge_number: firstElement?.judge_number || "",
            backup_judge_name: firstElement?.backup_judge_name || "",
            backup_judge_number: firstElement?.backup_judge_number || ""
          }];
        } else {
          _judges = [{
            judge_element: "",
            judge_name: sameJudgeValuesRef.current.judge_name,
            judge_number: sameJudgeValuesRef.current.judge_number,
            backup_judge_name: sameJudgeValuesRef.current.backup_judge_name,
            backup_judge_number: sameJudgeValuesRef.current.backup_judge_number
          }];
        }

        setJudges(_judges);
      } else {
        let _judges: IJudge[] = [];

        if (props.show) {
          _judges = getJudges();
        } else {
          _judges = props.showElements.map((se: any) => {
            return {
              judge_element: se.label,
              judge_name: se.judge_name || "",
              judge_number: se.judge_number || "",
              backup_judge_name: se.backup_judge_name || "",
              backup_judge_number: se.backup_judge_number || ""
            };
          });
        }

        setJudges(_judges);

        // init judges data. use values (name, number) from show_element records for existing show
        const _shouldUseNestedSetup = shouldUseNestedSetup(props.showType, props.event.sanctioning_club);
        
        if (_shouldUseNestedSetup) {
          const _elements = getShowElementsByNested(props.showElementsNested, _judges, undefined, props.show);

          const __judges: IJudge[] = _elements.map((se) => {
            return {
              judge_element: se.show_element,
              judge_level: se.level,
              judge_section: se.section,
              judge_name: se.judge_name ?? "",
              judge_number: se.judge_number ?? "",
              backup_judge_name: se.backup_judge_name ?? "",
              backup_judge_number: se.backup_judge_number ?? ""
            };
          });

          setJudges(__judges);
        }
      }
    }



  }, [props.showElements, props.showElementsNested, useSameJudge, props.show, ignoreJudges, props.showType]);

  let { onChange } = props;

  useEffect(() => {
    // For the onChange callback, we'll pass the judges array which now includes backup judge information
    // The consumer of this component will need to merge the backup judge info back into show_elements
    onChange(judges, useSameJudge, breedGroups, bsList);
  }, [judges, useSameJudge, onChange, breedGroups, bsList]);

  const getBreedGroups = useCallback(() => {
    return new Promise((resolve) => {
      let _query:any = {
        sort: [{
          attribute_name: "value",
          sort: "asc"
        }]
      };

      apiService.getBreedGroups(props.event.sanctioning_club, _query).then((response) => {
        resolve(response);
      }).catch((e) => {
        resolve(breedErrors.NO_DATA);
      });
    });
  }, [props.event.sanctioning_club]);

  // UKC Conformation: when "Total Dog" is offered, "Total Junior" is judged
  // as part of that same overall comparison and doesn't need its own judge.
  const isTotalDogChecked = () => {
    return showElements.some((se: ICheck) => se.label === Constants.dog_class_element.total_dog && se.checked === true);
  };

  const isJudgeElementHidden = (element: string): boolean => {
    if (HIDDEN_JUDGE_ELEMENTS.has(element)) {
      return true;
    }
    return element === Constants.dog_class_element.total_junior && isTotalDogChecked();
  };

  const renderElementJudges = () => {
    if (ignoreJudges.includes(props.showType)) {
      return (
        <p>Judges are not required for this show type.</p>
      );
    }

    return ( <> 
      {usesBreedGroups() &&
      <p>Element Judges are required. If you don't provide any breed group or breed specific judges, the element judge will be shown.</p>}
      <div className="judgeCheckWrap">
        <Checkbox id="sameJudge" label="Use the same judge for all elements, classes, and levels" value={useSameJudge} onChange={(e:any) => {
          if (useSameJudge && judges.length > 0 && judges[0].judge_element === "") {
            // Save current single-judge values before switching to per-element mode
            sameJudgeValuesRef.current = {
              judge_name: judges[0].judge_name,
              judge_number: judges[0].judge_number,
              backup_judge_name: judges[0].backup_judge_name ?? "",
              backup_judge_number: judges[0].backup_judge_number ?? ""
            };
          }
          setUseSameJudge(!useSameJudge);
        }} />
      </div>
      {judges.map((j: IJudge, index: number) => {

        const _useNested1 = shouldUseNestedSetup(props.showType, props.event.sanctioning_club);

        if (_useNested1) {
          const label = useSameJudge ? "" : [j.judge_element, j.judge_level, j.judge_section].join(" - ");
          const isJudgeNumberOptional = props.event.sanctioning_club === Constants.sanctioning_club.PWDCA || 
                                        props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS;

          return (
            <div key={"judge" + index} className="judgeInfo">
              <Input
                key={"name" + index}
                label={label + " Judge Name"}
                id={label + "judgeName"}
                type="text"
                placeholder={"What's the " + (label || "") + " judges name?"}
                defaultValue={j.judge_name}
                onChange={(e) => {
                  updateJudge({ judge_name: e.target.value }, index);
                }}
              />
              <Input
                key={"num" + index}
                label={"Judge #" + (isJudgeNumberOptional ? " (optional)" : "")}
                id={j.judge_element + "judgeNum"}
                type="text"
                defaultValue={j.judge_number}
                onChange={(e) => {
                  updateJudge({ judge_number: e.target.value }, index);
                }}
              />
            </div>
          );
        } else {
          let hasShowElementChecked = showElements.filter((se: ICheck) => {
            return se.label === j.judge_element && se.checked === true;
          });

          if ((j.judge_element === "" || hasShowElementChecked.length > 0) &&
              !isJudgeElementHidden(j.judge_element)) {
            const isJudgeNumberOptional = props.event.sanctioning_club === Constants.sanctioning_club.PWDCA || 
                                          props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS;
            const displayLabel = getJudgeElementLabel(j.judge_element);

            return (
              <div key={"judge" + index} className="judgeInfo">
                <Input
                  key={"name" + index}
                  label={displayLabel + " Judge Name"}
                  id={j.judge_element + "judgeName"}
                  type="text"
                  placeholder={"What's the " + (displayLabel?.toLowerCase() || "") + " judges name?"}
                  defaultValue={j.judge_name}
                  onChange={(e) => {
                    updateJudge({ judge_name: e.target.value }, index);
                  }}
                />
                <Input
                  key={"num" + index}
                  label={"Judge #" + (isJudgeNumberOptional ? " (optional)" : "")}
                  id={j.judge_element + "judgeNum"}
                  type="text"
                  defaultValue={j.judge_number}
                  onChange={(e) => {
                    updateJudge({ judge_number: e.target.value }, index);
                  }}
                />
              </div>
            );
          } else {
            return (
              <div key={index}></div>
            );
          }
        }

      })}

      {hasBackupJudges() && <>
        <div className="stepTitle">Backup Judges</div>
        <p>Backup judges will be used if the primary judge is unavailable.</p>
        {judges.map((j: IJudge, index: number) => {
          const _useNested2 = shouldUseNestedSetup(props.showType, props.event.sanctioning_club);

          if (_useNested2) {
            const label = useSameJudge ? "" : [j.judge_element, j.judge_level, j.judge_section].join(" - ");
            const isJudgeNumberOptional = props.event.sanctioning_club === Constants.sanctioning_club.PWDCA ||
                                          props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS;

            return (
              <div key={"backup-judge" + index} className="judgeInfo">
                <Input
                  key={"backup-name" + index}
                  label={label + " Backup Judge Name"}
                  id={label + "backupJudgeName"}
                  type="text"
                  placeholder={"What's the " + (label || "") + " backup judge's name?"}
                  defaultValue={j.backup_judge_name}
                  onChange={(e) => {
                    updateBackupJudge({ backup_judge_name: e.target.value }, index);
                  }}
                />
                <Input
                  key={"backup-num" + index}
                  label={"Backup Judge #" + (isJudgeNumberOptional ? " (optional)" : "")}
                  id={j.judge_element + "backupJudgeNum"}
                  type="text"
                  defaultValue={j.backup_judge_number}
                  onChange={(e) => {
                    updateBackupJudge({ backup_judge_number: e.target.value }, index);
                  }}
                />
              </div>
            );
          } else {
            let hasShowElementChecked = showElements.filter((se: ICheck) => {
              return se.label === j.judge_element && se.checked === true;
            });

            if ((j.judge_element === "" || hasShowElementChecked.length > 0) &&
                !isJudgeElementHidden(j.judge_element)) {
              const isJudgeNumberOptional = props.event.sanctioning_club === Constants.sanctioning_club.PWDCA ||
                                            props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS;
              const displayLabel = getJudgeElementLabel(j.judge_element);

              return (
                <div key={"backup-judge" + index} className="judgeInfo">
                  <Input
                    key={"backup-name" + index}
                    label={displayLabel + " Backup Judge Name"}
                    id={j.judge_element + "backupJudgeName"}
                    type="text"
                    placeholder={"What's the " + (displayLabel?.toLowerCase() || "") + " backup judge's name?"}
                    defaultValue={j.backup_judge_name}
                    onChange={(e) => {
                      updateBackupJudge({ backup_judge_name: e.target.value }, index);
                    }}
                  />
                  <Input
                    key={"backup-num" + index}
                    label={"Backup Judge #" + (isJudgeNumberOptional ? " (optional)" : "")}
                    id={j.judge_element + "backupJudgeNum"}
                    type="text"
                    defaultValue={j.backup_judge_number}
                    onChange={(e) => {
                      updateBackupJudge({ backup_judge_number: e.target.value }, index);
                    }}
                  />
                </div>
              );
            } else {
              return (
                <div key={"backup-index" + index}></div>
              );
            }
          }
        })}
      </>}

    </>);
  }

  useEffect(() => {
    if (!loaded) {
      const loadJudgesData = async() => {
        try {
          let breeds:any = await getBreedGroups();

          if (breeds !== breedErrors.NO_DATA) {
            let _breeds:BreedGroupJudgeDto[] = [];

            for (let b in breeds) {
              if (
                props.event.sanctioning_club === Constants.sanctioning_club.UKC &&
                UKC_HIDDEN_JUDGE_BREED_GROUPS.has(breeds[b].value)
              ) {
                continue;
              }
              _breeds.push({
                breed_group: breeds[b].value,
                judge_name: "",
                judge_number: ""
              });
            }

            if (props.show) {
              if (props.show?.breed_group_judges) {
                // Group saved entries by breed_group (may have multiple for alpha-split groups)
                const savedByGroup = new Map<string, typeof props.show.breed_group_judges>();
                for (const bgj of props.show.breed_group_judges) {
                  if (!savedByGroup.has(bgj.breed_group)) savedByGroup.set(bgj.breed_group, []);
                  savedByGroup.get(bgj.breed_group)!.push(bgj);
                }

                const finalBreeds: BreedGroupJudgeDto[] = [];
                const inserted = new Set<string>();
                for (const row of _breeds) {
                  const saved = savedByGroup.get(row.breed_group);
                  if (!saved || saved.length === 0) {
                    finalBreeds.push(row);
                  } else if (saved.length === 1) {
                    finalBreeds.push({
                      breed_group: row.breed_group,
                      judge_name: saved[0].judge_name || "",
                      judge_number: saved[0].judge_number || "",
                      start_letter: saved[0].start_letter,
                      end_letter: saved[0].end_letter,
                    });
                  } else {
                    // Multiple saved entries — insert all on first encounter of this group
                    if (!inserted.has(row.breed_group)) {
                      inserted.add(row.breed_group);
                      for (const s of saved) {
                        finalBreeds.push({
                          breed_group: s.breed_group,
                          judge_name: s.judge_name || "",
                          judge_number: s.judge_number || "",
                          start_letter: s.start_letter,
                          end_letter: s.end_letter,
                        });
                      }
                    }
                    // Skip subsequent seeded rows for this group (already inserted above)
                  }
                }
                _breeds = finalBreeds;
              }
            }

            setBreedGroups(_breeds);
          }
        } finally {
          // Always mark loaded after the async completes (success, NO_DATA, or error).
          // Moving this inside the async block ensures onChange never fires with
          // breedGroups = [] before the API has returned.
          setLoaded(true);
        }
      }

      loadJudgesData();
    }
  }, [loaded, props.event.sanctioning_club, getBreedGroups, props.show]);

  if (props.readOnly && !allowEditForConformation()) {
    const uniqueJudges = props.show ? judgesListedOnShow(props.show) : [];

    return (
      <div className={styles.JudgesTab}>
        <div className="stepTitle">Judges</div>
        <div className={styles.readOnlyNotice}>
          <strong>Judge assignments cannot be changed here after a show has been created.</strong> To add, remove, or reassign judges, please use the <strong>Judge Schedules</strong> tab in the show management area.{props.onNavigateToJudgeSchedules && <> <span className={styles.readOnlyLink} onClick={props.onNavigateToJudgeSchedules}>Open Judge Schedules →</span></>}
        </div>
        {uniqueJudges.length > 0 && (
          <>
            <p>Current judge assignments:</p>
            {uniqueJudges.map((j: any, index: number) => (
              <div key={index} className={styles.readOnlyJudge}>
                <span className={styles.readOnlyJudgeName}>{j.judge_name}</span>
                {j.judge_number && <span className={styles.readOnlyJudgeNumber}>#{j.judge_number}</span>}
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  return (
    <div className={styles.JudgesTab}>
      <div className="stepTitle">Judges</div>
      <p>Who are the judges for your show?</p>
      {usesBreedGroups() && <>
        <Accordion bellows={bellows} activeBellow={activeBellow} onBellowClick={(index: number) => { switchToBellow(index); }}>
          <Bellow>
            {renderElementJudges()}
          </Bellow>
          <Bellow>
            <>
              <p>Breed Group Judges are optional. Breed Group Judges will replace Element Judges where applicable on generated forms. If you don't have a judge for a specific breed group, you can leave it empty.</p>
              {(() => {
                const isJudgeNumberOptional = props.event.sanctioning_club === Constants.sanctioning_club.PWDCA ||
                                              props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS;

                // Count rows per group for +/- visibility
                const groupCounts = new Map<string, number>();
                breedGroups.forEach(b => groupCounts.set(b.breed_group, (groupCounts.get(b.breed_group) || 0) + 1));

                // Track position within each group for rendering
                const groupPos = new Map<string, number>();

                return breedGroups.map((b: BreedGroupJudgeDto, index: number) => {
                  const count = groupCounts.get(b.breed_group) || 1;
                  const pos = groupPos.get(b.breed_group) || 0;
                  groupPos.set(b.breed_group, pos + 1);
                  const isLastInGroup = pos === count - 1;
                  const isSplit = count > 1;

                  return (
                    <div key={`bg-${b.breed_group}-${pos}`} className={styles.bgJudgeRow}>
                      {/* Line 1: judge inputs + action buttons */}
                      <div className={styles.bgJudgeMain}>
                        <div className={styles.bgJudgeInputs}>
                          <Input
                            key={"name" + index}
                            label={b.breed_group + " Judge Name"}
                            id={b.breed_group + "judgeName" + pos}
                            type="text"
                            onChange={(e) => { updateBreedGroupJudge({ judge_name: e.target.value }, index); }}
                            placeholder={"What's the " + (b.breed_group?.toLowerCase() || "") + " judges name?"}
                            defaultValue={b.judge_name}
                          />
                          <Input
                            key={"num" + index}
                            label={"Judge #" + (isJudgeNumberOptional ? " (optional)" : "")}
                            id={b.breed_group + "judgeNum" + pos}
                            type="text"
                            onChange={(e) => { updateBreedGroupJudge({ judge_number: e.target.value }, index); }}
                            defaultValue={b.judge_number}
                          />
                        </div>
                        <div className={styles.bgRowActions}>
                          {isSplit && (
                            <button
                              type="button"
                              className={`${styles.bgRowBtn} ${styles.remove}`}
                              title="Remove this judge"
                              onClick={() => removeBreedGroupRow(index)}
                            ><span className={styles.bgRowBtnIcon} /></button>
                          )}
                          {isLastInGroup && count < MAX_SPLITS && (
                            <button
                              type="button"
                              className={`${styles.bgRowBtn} ${styles.add}`}
                              title="Add another judge for this breed group"
                              onClick={() => addBreedGroupRow(b.breed_group)}
                            ><span className={styles.bgRowBtnIcon} /></button>
                          )}
                        </div>
                      </div>
                      {/* Line 2 (split only): letter range pickers */}
                      {isSplit && (
                        <div className={styles.bgLetterRow}>
                          <span className={styles.bgLetterLabel}>Breeds:</span>
                          <div className={styles.bgLetterSelect}>
                            <label>Start</label>
                            <select
                              value={b.start_letter || "A"}
                              onChange={(e) => updateBreedGroupLetter(index, "start_letter", e.target.value)}
                            >
                              {LETTERS.map(l => <option key={l} value={l}>{l}</option>)}
                            </select>
                          </div>
                          <span className={styles.bgLetterDash}>–</span>
                          <div className={styles.bgLetterSelect}>
                            <label>End</label>
                            <select
                              value={b.end_letter || "Z"}
                              onChange={(e) => updateBreedGroupLetter(index, "end_letter", e.target.value)}
                            >
                              {LETTERS.map(l => <option key={l} value={l}>{l}</option>)}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </>
          </Bellow>
          <Bellow>
            <>
              <p>Breed Specific Judges are optional. Breed Specific Judges will replace both Breed Group and Element Judges where applicable on generated forms.</p>
              {<SearchDown
                key={`bsBreed-${bsInputsKey}`}
                label="Breed"
                id="breed"
                type="text"
                onChange={(e) => {
                  e.stopPropagation();
                  setBreedValue(e.target.value);
                  setBreedExists(false);
                }}
                onSelect={(value: string) => {
                  setBreedValue(value);
                  setBreedList([]);
                  setBreedExists(true);
                }}
                placeholder="What's the dog breed?"
                onSearch={searchBreeds}
                searchResults={breedList}
                defaultValue={breedValue}
              />}
              <div className={"judgeInfo breedSpecific"}>
                <Input key={`bsJudgeName-${bsInputsKey}`} label={"Judge Name"} id={"bsJudgeName"} type="text" onChange={(e) => { setBsJudgeName(e.target.value); }} placeholder="What's the judges name?" defaultValue={bsJudgeName} />
                <Input key={`bsJudgeNum-${bsInputsKey}`} label={"Judge #"} id={"bsJudgeNum"} type="text" onChange={(e) => { setBsJudgeNumber(e.target.value); }} defaultValue={bsJudgeNumber} />
              </div>
              <div className={styles.bsActions}>
                <Button onClick={() => { addBreedSpecificJudge(breedValue, bsJudgeName, bsJudgeNumber); }} type="button">Add Judge</Button>
              </div>
              {bsList.length > 0 && <div className={styles.bsList}>
                <div className={`${styles.bsListItem} ${styles.bsListHeaders}`}>
                  <span>Breed</span>
                  <span>Judge Name</span>
                  <span>Judge Number</span>
                  <span></span>
                </div>
                {bsList.map((b: BreedJudgeDto, index: number) => {
                  return <div key={index} className={styles.bsListItem}>
                    <span>{b.breed}</span>
                    <span>{b.judge_name}</span>
                    <span>{b.judge_number}</span>
                    <div className={styles.bsListActions}>
                      <span onClick={() => { deleteBreedSpecificJudge(index); }} className={`${styles.icon} ${styles.delete}`}></span>
                    </div>
                  </div>;
                })}
              </div>}
            </>
          </Bellow>
          <Bellow>
            <>
              {hasBackupJudges() && <>
                <p>Backup Judges are optional. Backup judges will be used if the primary judge is unavailable.</p>
                {judges.map((j: IJudge, index: number) => {
                  const _useNested3 = shouldUseNestedSetup(props.showType, props.event.sanctioning_club);

                  if (_useNested3) {
                    const label = useSameJudge ? "" : [j.judge_element, j.judge_level, j.judge_section].join(" - ");
                    const isJudgeNumberOptional = props.event.sanctioning_club === Constants.sanctioning_club.PWDCA ||
                                          props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS;

                    return (
                      <div key={"backup-judge" + index} className="judgeInfo">
                        <Input
                          key={"backup-name" + index}
                          label={label + " Backup Judge Name"}
                          id={label + "backupJudgeName"}
                          type="text"
                          placeholder={"What's the " + (label || "") + " backup judge's name?"}
                          defaultValue={j.backup_judge_name}
                          onChange={(e) => {
                            updateBackupJudge({ backup_judge_name: e.target.value }, index);
                          }}
                        />
                        <Input
                          key={"backup-num" + index}
                          label={"Backup Judge #" + (isJudgeNumberOptional ? " (optional)" : "")}
                          id={j.judge_element + "backupJudgeNum"}
                          type="text"
                          defaultValue={j.backup_judge_number}
                          onChange={(e) => {
                            updateBackupJudge({ backup_judge_number: e.target.value }, index);
                          }}
                        />
                      </div>
                    );
                  } else {
                    let hasShowElementChecked = showElements.filter((se: ICheck) => {
                      return se.label === j.judge_element && se.checked === true;
                    });

                    if ((j.judge_element === "" || hasShowElementChecked.length > 0) &&
                        !isJudgeElementHidden(j.judge_element)) {
                      const isJudgeNumberOptional = props.event.sanctioning_club === Constants.sanctioning_club.PWDCA ||
                                            props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS;
                      const displayLabel = getJudgeElementLabel(j.judge_element);

                      return (
                        <div key={"backup-judge" + index} className="judgeInfo">
                          <Input
                            key={"backup-name" + index}
                            label={displayLabel + " Backup Judge Name"}
                            id={j.judge_element + "backupJudgeName"}
                            type="text"
                            placeholder={"What's the " + (displayLabel?.toLowerCase() || "") + " backup judge's name?"}
                            defaultValue={j.backup_judge_name}
                            onChange={(e) => {
                              updateBackupJudge({ backup_judge_name: e.target.value }, index);
                            }}
                          />
                          <Input
                            key={"backup-num" + index}
                            label={"Backup Judge #" + (isJudgeNumberOptional ? " (optional)" : "")}
                            id={j.judge_element + "backupJudgeNum"}
                            type="text"
                            defaultValue={j.backup_judge_number}
                            onChange={(e) => {
                              updateBackupJudge({ backup_judge_number: e.target.value }, index);
                            }}
                          />
                        </div>
                      );
                    } else {
                      return (
                        <div key={"backup-index" + index}></div>
                      );
                    }
                  }
                })}
              </>}
            </>
          </Bellow>
        </Accordion>
      </>}
      {!usesBreedGroups() && renderElementJudges()}
    </div>
  );
};

export default JudgesTab;